import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { makeTrade } from "../redux/tradeSlice";

const TradeNow = ({ userId }) => {
  const dispatch = useDispatch();
  const [latestDate, setLatestDate] = useState(null);
  const [canShowButton, setCanShowButton] = useState(false);
  const [countdown, setCountdown] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isWeekend, setIsWeekend] = useState(false);
  const { loading, message } = useSelector((state) => state.trade);

  // Check if today is weekend (Saturday or Sunday)
  useEffect(() => {
    const checkIfWeekend = () => {
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 is Sunday, 6 is Saturday
      setIsWeekend(dayOfWeek === 0 || dayOfWeek === 6);
    };
    
    checkIfWeekend();
    // Check every minute in case day changes during session
    const interval = setInterval(checkIfWeekend, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchLatestRoiDate = async () => {
      try {
        const res = await axios.get(
          `https://api.goldfoxmarket.com/api/v1/referral/latest/${userId}`
        );

        if (res.data?.latestRoiDate) {
          const roiDate = new Date(res.data.latestRoiDate);
          setLatestDate(roiDate);
        } else {
          setCanShowButton(true); // If API returns success but no date
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setCanShowButton(true); // Show button if not found
        } else {
          console.error("Error fetching ROI date:", err);
        }
      }
    };

    fetchLatestRoiDate();
  }, [userId]);

  useEffect(() => {
    if (!latestDate) return;

    const timer = setInterval(() => {
      const now = new Date();
      const nextRoiTime = new Date(latestDate.getTime() + 24 * 60 * 60 * 1000);
      const diff = nextRoiTime - now;

      if (diff <= 0) {
        setCanShowButton(true);
        setCountdown("");
        clearInterval(timer);
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);

        setCountdown(
          `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
        );
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [latestDate]);

  useEffect(() => {
    if (message) {
      setShowSuccessMessage(true);
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        window.location.reload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  async function handleTrade() {
    dispatch(makeTrade(userId));
  }

  function getWeekendDates(date = new Date()) {
    const day = date.getDay(); // 0 (Sun) to 6 (Sat)
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - day); // Go back to Sunday

    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6); // Move to Saturday

    return {
      sunday: sunday.toDateString(),
      saturday: saturday.toDateString(),
    };
  }

  // Get next available trading day message
  function getNextTradingDayMessage() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    
    if (dayOfWeek === 0) { // Sunday
      return "Trading resumes on Monday";
    } else if (dayOfWeek === 6) { // Saturday
      return "Trading resumes on Monday";
    }
    return "";
  }

  // Determine if button should be shown (not weekend and passed other checks)
  const shouldShowButton = canShowButton && !isWeekend;

  return (
    <div className="mt-8">
      {/* Success Message Animation */}
      {showSuccessMessage && (
        <div className="flex justify-center mb-6 animate-fade-in">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-lg p-4 flex items-center space-x-3 max-w-md">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium">
                {message || "Your ROI has been claimed successfully!"}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="text-center">
   
        {isWeekend ? (
          <div className="bg-gradient-to-br from-[#1f1f3d] to-[#121223] rounded-md px-2 py-3 border border-gray-700 shadow-lg text-white">
            <div className="flex flex-col items-center space-y-3">
              <h3 className="text-xs uppercase tracking-widest text-gray-400 font-medium">
                Weekend Trading Unavailable
              </h3>
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-[#2a2a4d] rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="text-base font-semibold text-yellow-400 tracking-wide">
                  {getNextTradingDayMessage()}
                </div>
              </div>
            </div>
          </div>
        ) : shouldShowButton ? (
          <button
            onClick={handleTrade}
            disabled={loading}
            className="relative inline-flex items-center justify-center overflow-hidden rounded-lg px-8 py-3 font-medium group w-full"
          >
            <span className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600"></span>
            <span className="absolute bottom-0 right-0 block w-64 h-64 mb-32 mr-4 transition duration-500 origin-bottom-left transform rotate-45 translate-x-24 bg-blue-400 rounded-full opacity-30 group-hover:rotate-90 ease"></span>
            <span className="relative flex items-center justify-center w-full text-white transition-all duration-200 ease-out">
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  Trade Now
                </>
              )}
            </span>
          </button>
        ) : countdown ? (
          <div className="bg-gradient-to-br from-[#1f1f3d] to-[#121223] rounded-md px-2 py-3 border border-gray-700 shadow-lg text-white">
            <div className="flex flex-col items-center space-y-3">
              <h3 className="text-xs uppercase tracking-widest text-gray-400 font-medium">
                Next Trade Available In
              </h3>
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-[#2a2a4d] rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="text-base font-semibold text-emerald-400 tracking-wide">
                  {countdown}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center py-4">
            <div className="animate-pulse flex space-x-2">
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeNow;